import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, Input, Select, Switch, Textarea } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  SalesChannelClient,
  type InventoryFeedFormat,
  type SalesChannelDTO,
  type SalesChannelTransport,
} from "../api/inventory";
import { clientLabel, NexpimClientClient } from "../api/pricing";
import type { SigmClientDTO } from "../api/reporting";
import { NexpimView } from "../views";

const TRANSPORTS: SalesChannelTransport[] = ["API", "MAIL", "EDI"];
const FEED_FORMATS: InventoryFeedFormat[] = [
  "WAYFAIR_API",
  "SHOPIFY_API",
  "OVERSTOCK_API",
  "BISON_XLSX",
  "LINEN_CHEST_XLSX",
  "MEUBLES_RD_XLSX",
  "STAPLES_XLSX",
  "SIMONS_CSV",
  "AMAZON_VENDOR_CSV",
  "TANGUAY_XLSX",
  "TEAM_US_XLSX",
  "MAIL_LIST_XLSX",
];

/**
 * The sales channels: everything about a platform that is data — which SIGM
 * client it bills (and so which market), how the feed travels, who receives
 * the mail, the supplier/location ids, the required metafield, the delisting
 * threshold. The one thing that stays code is the feed format: it names a
 * writer, and a writer is code, so the list here is the writers that exist.
 *
 * No market field: the server derives it from the client's currency and sends
 * it back read-only. A channel whose client cannot be found shows no market
 * and cannot run.
 *
 * Gated by the inventory-updates view rather than a code of its own — the
 * people who run the feeds are the people who configure them.
 */
export function SalesChannelsPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const queryClient = useQueryClient();

  const client = useMemo(() => new SalesChannelClient(http), [http]);
  const clients = useMemo(() => new NexpimClientClient(http), [http]);
  const allowed = hasView(NexpimView.API_INVENTORY_UPDATES);

  const [editing, setEditing] = useState<SalesChannelDTO | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["sales-channel", "all"],
    queryFn: () => client.findAll(),
    enabled: allowed,
  });
  // Every SIGM client, not only the active ones: a channel may bill a client
  // SIGM has since deactivated (Amazon.com Vendor does), and the picker must
  // still show it.
  const { data: sigmClients } = useQuery({
    queryKey: ["client", "all"],
    queryFn: () => clients.findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["sales-channel"] });

  /** Drop = the new order; renumbered in tens and saved for the rows that moved. */
  const reorder = async (next: SalesChannelDTO[]) => {
    const changed = next
      .map((row, index) => ({ row, wanted: (index + 1) * 10 }))
      .filter(({ row, wanted }) => row.sort_order !== wanted)
      .map(({ row, wanted }) => ({ ...row, sort_order: wanted }));
    try {
      for (const row of changed) await client.update(row);
      await queryClient.invalidateQueries({ queryKey: ["sales-channel"] });
      toast.success(t("config.order.saved", "Order saved"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Something went wrong. Please try again."));
    }
  };

  const save = useMutation({
    mutationFn: async (channel: SalesChannelDTO) => {
      if (channel.id) {
        await client.update(channel);
        return channel;
      }
      return client.create(channel);
    },
    onSuccess: async () => {
      setEditing(null);
      await refresh();
      toast.success(t("notification.saved", "Saved"));
    },
    onError: (failure) => toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const cols = useMemo(() => {
    const c = defineColumns<SalesChannelDTO>();
    return [
      c.text("name", (row) => row.name, { headerKey: "sales-channel.name", priority: 1 }),
      c.text("code", (row) => row.code, { headerKey: "sales-channel.code", priority: 2 }),
      c.custom(
        "market",
        (row) => row.market ?? "",
        (row) =>
          row.market ? (
            <Badge size="sm">{row.market}</Badge>
          ) : (
            <Badge variant="error" size="sm">
              {t("sales-channel.no-market", "no market")}
            </Badge>
          ),
        { headerKey: "sales-channel.market", filter: "select", priority: 1 },
      ),
      c.text("client", (row) => row.client?.cli_name1 ?? `#${row.nexera_client_id}`, {
        headerKey: "sales-channel.client",
        priority: 2,
      }),
      c.text("transport", (row) => row.transport, { headerKey: "sales-channel.transport", filter: "select", priority: 3 }),
      c.text("format", (row) => row.feed_format, { headerKey: "sales-channel.format", filter: "select", priority: 3 }),
      c.custom(
        "active",
        (row) => (row.active ? "yes" : "no"),
        (row) => (
          <Badge variant={row.active ? "success" : "neutral"} size="sm">
            {row.active ? t("sales-channel.active", "active") : t("sales-channel.inactive", "inactive")}
          </Badge>
        ),
        { headerKey: "sales-channel.active-column", filter: "select", priority: 1 },
      ),
      c.text(
        "default_floor",
        (row) => (row.default_floor_quantity ? String(row.default_floor_quantity) : t("sales-channel.default-floor.none", "No floor")),
        { headerKey: "sales-channel.default-floor", filter: "select", priority: 2 },
      ),
      c.text(
        "lists_new",
        (row) => (row.is_listing_new_products ? t("sales-channel.lists-new.yes", "Listed") : t("sales-channel.lists-new.no", "Chosen by hand")),
        { headerKey: "sales-channel.lists-new", filter: "select", priority: 3 },
      ),
      c.text(
        "delist",
        (row) => (row.delist_after_sends ? String(row.delist_after_sends) : t("sales-channel.delist.never", "never")),
        { headerKey: "sales-channel.delist", priority: 3 },
      ),
      c.number("recipients", (row) => row.mail_to.length + row.mail_cc.length + row.mail_bcc.length, {
        headerKey: "sales-channel.recipients",
        priority: 4,
      }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.sales-channels", "Sales channels")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.sales-channels", "Sales channels")}
        subtitle={t("sales-channel.subtitle", "The platforms the inventory feeds target: client, transport, writer, recipients, delisting.")}
      />
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        selectable
        onReorder={(next) => void reorder(next)}
        onRowClick={(row) => setEditing({ ...row })}
        actions={[
          {
            label: t("sales-channel.new", "New channel"),
            icon: <Plus className="size-4" />,
            variant: "primary",
            write: true,
            onClick: () => setEditing(blank((data ?? []).length)),
          },
        ]}
        onDeleteSelected={async (rows) => {
          await client.removeBatch(rows.map((row) => row.id));
          await refresh();
          toast.success(t("notification.deleted", "Deleted"));
        }}
        deleteSelectedConfirm={(count) => ({
          title: t("sales-channel.delete.title", "Delete {{count}} channel(s)?", { count }),
          message: t(
            "sales-channel.delete.message",
            "Every product rule and every recorded run of these channels goes with them. Deactivate instead to keep the history.",
          ),
          requireText: "DELETE",
        })}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
      {editing && (
        <ChannelDialog
          channel={editing}
          clients={sigmClients ?? []}
          saving={save.isPending}
          onClose={() => setEditing(null)}
          onSave={(channel) => save.mutate(channel)}
        />
      )}
    </>
  );
}

function blank(count: number): SalesChannelDTO {
  return {
    id: 0,
    code: "",
    name: "",
    nexera_client_id: 0,
    transport: "MAIL",
    feed_format: "MAIL_LIST_XLSX",
    active: false,
    sort_order: (count + 1) * 10,
    external_supplier_id: null,
    external_location_id: null,
    required_metafield: null,
    mail_to: [],
    mail_cc: [],
    mail_bcc: [],
    mail_subject: "Nexera inventory - {date}",
    mail_attachment: "nexera-inventory-{date}.xlsx",
    delist_after_sends: 5,
    default_floor_quantity: 10,
    is_listing_new_products: true,
  };
}

/** One address per line; blank lines and whitespace dropped. */
function parseAddresses(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function ChannelDialog({
  channel,
  clients,
  saving,
  onClose,
  onSave,
}: {
  channel: SalesChannelDTO;
  clients: SigmClientDTO[];
  saving: boolean;
  onClose: () => void;
  onSave: (channel: SalesChannelDTO) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SalesChannelDTO>(channel);
  const [mailTo, setMailTo] = useState(channel.mail_to.join("\n"));
  const [mailCc, setMailCc] = useState(channel.mail_cc.join("\n"));
  const [mailBcc, setMailBcc] = useState(channel.mail_bcc.join("\n"));
  const patch = (next: Partial<SalesChannelDTO>) => setDraft((current) => ({ ...current, ...next }));

  const clientOptions = useMemo(
    () =>
      [...clients]
        .sort((a, b) => (a.cli_name1 ?? "").localeCompare(b.cli_name1 ?? ""))
        .map((row) => ({
          value: String(row.cli_id),
          label: `${clientLabel(row)}${row.cli_active === false ? ` (${t("sales-channel.client.inactive", "inactive in SIGM")})` : ""}`,
        })),
    [clients, t],
  );
  const selectedClient = clients.find((row) => row.cli_id === draft.nexera_client_id);
  const market = selectedClient?.cli_currency === "CAD" ? "CA" : selectedClient?.cli_currency === "USD" ? "US" : null;

  const isMail = draft.transport === "MAIL";
  const errors = {
    code: !/^[A-Z][A-Z0-9_]*$/.test(draft.code) ? t("sales-channel.error.code", "Upper-case letters, digits and underscores") : undefined,
    name: draft.name.trim() ? undefined : t("form.required", "Required"),
    client: draft.nexera_client_id ? (market ? undefined : t("sales-channel.error.currency", "This client's currency is not a market")) : t("form.required", "Required"),
    mailTo: isMail && parseAddresses(mailTo).length === 0 ? t("form.required", "Required") : undefined,
    mailSubject: isMail && !draft.mail_subject?.trim() ? t("form.required", "Required") : undefined,
    mailAttachment: isMail && !draft.mail_attachment?.trim() ? t("form.required", "Required") : undefined,
    defaultFloor:
      draft.default_floor_quantity != null && !(draft.default_floor_quantity >= 1)
        ? t("sales-channel.error.default-floor", "1 or more, or No floor")
        : undefined,
    delist: draft.delist_after_sends != null && draft.delist_after_sends < 1 ? t("sales-channel.error.delist", "1 or more, or empty for never") : undefined,
  };
  const invalid = Object.values(errors).some(Boolean);

  const submit = () =>
    onSave({
      ...draft,
      code: draft.code.trim(),
      name: draft.name.trim(),
      external_supplier_id: draft.external_supplier_id?.trim() || null,
      external_location_id: draft.external_location_id?.trim() || null,
      required_metafield: draft.required_metafield?.trim() || null,
      mail_to: parseAddresses(mailTo),
      mail_cc: parseAddresses(mailCc),
      mail_bcc: parseAddresses(mailBcc),
      mail_subject: isMail ? draft.mail_subject?.trim() ?? null : draft.mail_subject ?? null,
      mail_attachment: isMail ? draft.mail_attachment?.trim() ?? null : draft.mail_attachment ?? null,
    });

  return (
    <Dialog
      open
      onClose={onClose}
      title={channel.id ? channel.name : t("sales-channel.new", "New channel")}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t("button.cancel", "Cancel")}
          </Button>
          <Button variant="primary" loading={saving} disabled={invalid} onClick={submit}>
            {t("button.save", "Save")}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("sales-channel.code", "Code")} htmlFor="sc-code" required error={errors.code}
          hint={t("sales-channel.code.hint", "Stable key used by the cron scripts and the update URL")}>
          <Input id="sc-code" value={draft.code} invalid={!!errors.code} onChange={(e) => patch({ code: e.target.value.toUpperCase() })} />
        </Field>
        <Field label={t("sales-channel.name", "Name")} htmlFor="sc-name" required error={errors.name}>
          <Input id="sc-name" value={draft.name} invalid={!!errors.name} onChange={(e) => patch({ name: e.target.value })} />
        </Field>

        <Field
          label={t("sales-channel.client", "SIGM client")}
          htmlFor="sc-client"
          required
          error={errors.client}
          hint={market ? t("sales-channel.client.market", "Market {{market}}, from the client's currency", { market }) : t("sales-channel.client.hint", "The client's currency is the market: CAD → CA, USD → US")}
          className="sm:col-span-2"
        >
          <Select
            id="sc-client"
            value={draft.nexera_client_id ? String(draft.nexera_client_id) : ""}
            placeholder={t("sales-channel.client.placeholder", "Pick a client…")}
            options={clientOptions}
            invalid={!!errors.client}
            onChange={(e) => patch({ nexera_client_id: Number(e.target.value) })}
          />
        </Field>

        <Field label={t("sales-channel.transport", "Transport")} htmlFor="sc-transport">
          <Select id="sc-transport" value={draft.transport} options={TRANSPORTS.map((value) => ({ value, label: value }))}
            onChange={(e) => patch({ transport: e.target.value as SalesChannelTransport })} />
        </Field>
        <Field label={t("sales-channel.format", "Feed format")} htmlFor="sc-format" hint={t("sales-channel.format.hint", "Which writer builds the feed")}>
          <Select id="sc-format" value={draft.feed_format} options={FEED_FORMATS.map((value) => ({ value, label: value }))}
            onChange={(e) => patch({ feed_format: e.target.value as InventoryFeedFormat })} />
        </Field>

        <Field label={t("sales-channel.supplier-id", "External supplier id")} htmlFor="sc-supplier" hint={t("sales-channel.supplier-id.hint", "Wayfair supplier id")}>
          <Input id="sc-supplier" value={draft.external_supplier_id ?? ""} onChange={(e) => patch({ external_supplier_id: e.target.value })} />
        </Field>
        <Field label={t("sales-channel.location-id", "External location id")} htmlFor="sc-location" hint={t("sales-channel.location-id.hint", "Shopify location")}>
          <Input id="sc-location" value={draft.external_location_id ?? ""} onChange={(e) => patch({ external_location_id: e.target.value })} />
        </Field>
        <Field label={t("sales-channel.required-metafield", "Required metafield")} htmlFor="sc-metafield"
          hint={t("sales-channel.required-metafield.hint", "Only products carrying it are in the feed (Amazon ASIN, Staples merchant SKU)")}>
          <Input id="sc-metafield" value={draft.required_metafield ?? ""} onChange={(e) => patch({ required_metafield: e.target.value })} />
        </Field>
        <Field label={t("sales-channel.default-floor", "Default floor")} htmlFor="sc-default-floor" error={errors.defaultFloor}
          hint={t("sales-channel.default-floor.hint", "What the daily inventory rule job floors a market's top sellers at on this channel. No floor: this channel never floors.")}>
          <div className="flex items-center gap-3">
            <Switch
              checked={draft.default_floor_quantity != null}
              onChange={(on) => patch({ default_floor_quantity: on ? 10 : null })}
              label={draft.default_floor_quantity != null ? t("sales-channel.default-floor.at", "Floor at") : t("sales-channel.default-floor.none", "No floor")}
            />
            {draft.default_floor_quantity != null && (
              <Input id="sc-default-floor" type="number" min={1} className="w-24" value={draft.default_floor_quantity ?? ""} invalid={!!errors.defaultFloor}
                onChange={(e) => patch({ default_floor_quantity: e.target.value === "" ? 0 : Number(e.target.value) })} />
            )}
          </div>
        </Field>
        <Field label={t("sales-channel.lists-new", "New products")} htmlFor="sc-lists-new"
          hint={t("sales-channel.lists-new.hint", "Off where the catalogue is chosen by hand (Amazon): a new product starts not listed here.")}>
          <Switch
            checked={draft.is_listing_new_products}
            onChange={(is_listing_new_products) => patch({ is_listing_new_products })}
            label={t("sales-channel.lists-new.switch", "List new products automatically")}
          />
        </Field>
        <Field label={t("sales-channel.delist", "Delist after sends")} htmlFor="sc-delist" error={errors.delist}
          hint={t("sales-channel.delist.hint", "Discontinued SKUs go out this many successful runs, then leave the feed. Empty: always sent.")}>
          <Input id="sc-delist" type="number" min={1} value={draft.delist_after_sends ?? ""} invalid={!!errors.delist}
            onChange={(e) => patch({ delist_after_sends: e.target.value === "" ? null : Number(e.target.value) })} />
        </Field>

        {isMail && (
          <>
            <Field label={t("sales-channel.mail-to", "Mail to")} htmlFor="sc-to" required error={errors.mailTo} hint={t("sales-channel.mail.one-per-line", "One address per line")}>
              <Textarea id="sc-to" rows={3} value={mailTo} invalid={!!errors.mailTo} onChange={(e) => setMailTo(e.target.value)} />
            </Field>
            <Field label={t("sales-channel.mail-cc", "Cc")} htmlFor="sc-cc">
              <Textarea id="sc-cc" rows={3} value={mailCc} onChange={(e) => setMailCc(e.target.value)} />
            </Field>
            <Field label={t("sales-channel.mail-bcc", "Bcc")} htmlFor="sc-bcc" className="sm:col-span-2">
              <Textarea id="sc-bcc" rows={4} value={mailBcc} onChange={(e) => setMailBcc(e.target.value)} />
            </Field>
            <Field label={t("sales-channel.mail-subject", "Subject")} htmlFor="sc-subject" required error={errors.mailSubject} hint={t("sales-channel.mail.date", "{date} becomes the run date")}>
              <Input id="sc-subject" value={draft.mail_subject ?? ""} invalid={!!errors.mailSubject} onChange={(e) => patch({ mail_subject: e.target.value })} />
            </Field>
            <Field label={t("sales-channel.mail-attachment", "Attachment file name")} htmlFor="sc-attachment" required error={errors.mailAttachment}>
              <Input id="sc-attachment" value={draft.mail_attachment ?? ""} invalid={!!errors.mailAttachment} onChange={(e) => patch({ mail_attachment: e.target.value })} />
            </Field>
          </>
        )}

        <div className="flex items-center gap-6 sm:col-span-2">
          <Switch checked={draft.active} onChange={(active) => patch({ active })} label={t("sales-channel.active-switch", "Active: a run may deliver")} />
        </div>
      </div>
    </Dialog>
  );
}
